import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import {
    Edit3, MapPin, Sparkles, ArrowRight, Loader2
} from 'lucide-react';
import { PostCard, getInitials } from '../../components/reseau/PostCardKit';
import { PostComposer } from '../../components/reseau/PostComposer';
import { CommentsPanel } from '../../components/reseau/CommentsPanel';
import ReseauLayout from '../../components/reseau/ReseauLayout';

// ====================================================================
// Composants auxiliaires
// ====================================================================
function EmptyState({ state, user, onCompose }) {
    const actions = (state?.actions || []).filter(a => a && a.label);
    return (
        <div data-testid="empty-state" className="reseau-empty">
            <Sparkles className="w-10 h-10 mx-auto mb-3" style={{ color: 'var(--reseau-orange)' }} />
            <h3>
                {state?.role === "professional"
                    ? "Votre fil va se remplir bientôt"
                    : "Découvrez votre communauté locale"}
            </h3>
            <p>Voici quelques actions utiles pour démarrer.</p>
            <div className="space-y-2 mt-4">
                {actions.map(a => (
                    <button key={a.code}
                            onClick={a.code && a.code.includes("compose") ? onCompose : undefined}
                            data-testid={`empty-action-${a.code}`}
                            className="w-full text-left p-3 rounded-lg text-sm flex items-center justify-between transition reseau-card reseau-card-hoverable">
                        <span style={{ color: 'var(--reseau-ink-soft)' }}>{a.label}</span>
                        <ArrowRight className="w-4 h-4" style={{ color: 'var(--reseau-ink-muted)' }} />
                    </button>
                ))}
                {actions.length === 0 && user && ["agent","admin","super_admin","internal","group_manager"].includes(user.role) && (
                    <Button onClick={onCompose} data-testid="empty-publish-cta"
                            className="reseau-btn reseau-btn-primary">
                        Publier ma première contribution
                    </Button>
                )}
            </div>
        </div>
    );
}

function TerritoryIncitationCard({ user }) {
    if (!user) return null;
    return (
        <div data-testid="territory-incitation-card" className="reseau-card p-4 mb-4">
            <div className="flex items-center gap-2 mb-2">
                <MapPin className="w-4 h-4" style={{ color: 'var(--reseau-violet)' }} />
                <h4 className="font-semibold text-sm" style={{ color: 'var(--reseau-ink)' }}>
                    Aidez votre communauté locale à grandir
                </h4>
            </div>
            <p className="text-xs mb-3" style={{ color: 'var(--reseau-ink-muted)' }}>
                Invitez des professionnels de confiance et partagez des contenus
                réellement utiles aux vendeurs de votre secteur.
            </p>
            <Button variant="outline" size="sm" className="w-full text-xs reseau-btn reseau-btn-ghost">
                Voir les actions utiles
            </Button>
        </div>
    );
}

function CockpitDigest({ user }) {
    const [stats, setStats] = useState(null);
    useEffect(() => {
        if (!user || !["agent","admin","super_admin","internal","group_manager"].includes(user.role)) return;
        api.get("/posts/cockpit/stats?days=7").then(r => setStats(r.data)).catch(()=>{});
    }, [user]);
    if (!stats) return null;
    return (
        <div data-testid="cockpit-digest" className="reseau-card p-4">
            <h4 className="font-semibold text-sm mb-3" style={{ color: 'var(--reseau-ink)' }}>
                Votre activité (7 jours)
            </h4>
            <dl className="grid grid-cols-2 gap-2 text-xs">
                <div className="reseau-card-soft p-2 rounded-lg">
                    <dt style={{ color: 'var(--reseau-ink-muted)' }}>Publications</dt>
                    <dd className="font-semibold text-lg" style={{ color: 'var(--reseau-ink)' }}>
                        {stats.totals_7d?.publications ?? 0}
                    </dd>
                </div>
                <div className="reseau-card-soft p-2 rounded-lg">
                    <dt style={{ color: 'var(--reseau-ink-muted)' }}>Réactions</dt>
                    <dd className="font-semibold text-lg" style={{ color: 'var(--reseau-ink)' }}>
                        {stats.totals_7d?.reactions ?? 0}
                    </dd>
                </div>
            </dl>
            <Link to="/reseau/mes-publications"
                  data-testid="cockpit-digest-link"
                  className="block mt-3 text-xs text-center hover:underline"
                  style={{ color: 'var(--reseau-violet)' }}>
                Voir mes publications →
            </Link>
        </div>
    );
}

// ====================================================================
// Hook : récupération + mutations sociales
// ====================================================================
function useFeed(territoryFilter) {
    const [items, setItems] = useState([]);
    const [emptyState, setEmptyState] = useState(null);
    const [nextCursor, setNextCursor] = useState(null);
    const [loading, setLoading] = useState(true);
    const [savedIds, setSavedIds] = useState(new Set());
    const [userReactions, setUserReactions] = useState(new Map());

    const enrichItems = useCallback(async (rows) => {
        const tids = [...new Set(rows.map(it => it.territory_id).filter(Boolean))];
        if (tids.length) {
            try {
                const tr = await api.get(`/territories?limit=200`);
                const map = {};
                (tr.data.items || []).forEach(t => { map[t.id] = t.name; });
                rows.forEach(it => {
                    if (it.territory_id) it.territory_id_name = map[it.territory_id];
                });
            } catch { /* ignore */ }
        }
        return rows;
    }, []);

    const load = useCallback(async (cursor) => {
        setLoading(true);
        try {
            const url = territoryFilter
                ? `/feed?limit=10&territory_id=${territoryFilter}${cursor ? `&cursor=${cursor}` : ""}`
                : `/feed?limit=10${cursor ? `&cursor=${cursor}` : ""}`;
            const r = await api.get(url);
            const enriched = await enrichItems(r.data.items || []);
            enriched.forEach(it => {
                if (savedIds.has(it.content_id)) it._saved = true;
                if (userReactions.has(it.content_id)) it._userReaction = userReactions.get(it.content_id);
            });
            setItems(prev => cursor ? [...prev, ...enriched] : enriched);
            setNextCursor(r.data.next_cursor);
            setEmptyState(r.data.empty_state);
        } catch (e) {
            console.error(e);
        } finally { setLoading(false); }
    }, [territoryFilter, savedIds, userReactions, enrichItems]);

    return { items, setItems, emptyState, nextCursor, loading, load,
             savedIds, setSavedIds, userReactions, setUserReactions };
}

// ====================================================================
// Page principale
// ====================================================================
export default function ReseauFeedPage() {
    const [params] = useSearchParams();
    const territoryFilter = params.get("territoire");
    const composeOnLoad = params.get("compose") === "1";
    const [composerOpen, setComposerOpen] = useState(composeOnLoad);
    const [openCommentsFor, setOpenCommentsFor] = useState(null);
    const [user, setUser] = useState(null);
    const [territoryInfo, setTerritoryInfo] = useState(null);

    const f = useFeed(territoryFilter);
    const { items, setItems, emptyState, nextCursor, loading, load,
            savedIds, setSavedIds, userReactions, setUserReactions } = f;

    useEffect(() => {
        api.get("/auth/me").then(r => setUser(r.data)).catch(() => setUser(null));
    }, []);

    useEffect(() => {
        if (territoryFilter) {
            api.get(`/territories/${territoryFilter}`).then(r => setTerritoryInfo(r.data)).catch(()=>{});
        }
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [territoryFilter]);

    // ------------------------------------------------------------------
    // Actions sociales avec rollback strict (Lot Pré-bêta)
    // - optimistic update → catch → revert exact + toast retry-friendly
    // - guard `inFlight` pour éviter les doublons sur double-clic
    // ------------------------------------------------------------------
    const inFlightRef = React.useRef(new Set());
    const guard = (key) => {
        if (inFlightRef.current.has(key)) return false;
        inFlightRef.current.add(key);
        return true;
    };
    const release = (key) => { inFlightRef.current.delete(key); };

    const handleReact = async (post, reactionCode) => {
        const cid = post.content_id || post.id;
        const key = `react:${cid}`;
        if (!guard(key)) return;
        const prev = userReactions.get(cid);
        const prevSnapshot = items.find(it => it.content_id === cid);
        // optimistic
        setUserReactions(m => new Map(m).set(cid, reactionCode));
        setItems(arr => arr.map(it => it.content_id === cid
            ? {...it, _userReaction: reactionCode,
               metadata: {...it.metadata,
                  reactions_count: prev ? (it.metadata?.reactions_count || 0) : ((it.metadata?.reactions_count || 0) + 1) }}
            : it));
        try {
            await api.post(`/posts/${cid}/reactions`, { reaction_type: reactionCode });
        } catch (e) {
            // revert état exact
            setUserReactions(m => { const n = new Map(m); if (prev) n.set(cid, prev); else n.delete(cid); return n; });
            if (prevSnapshot) setItems(arr => arr.map(it => it.content_id === cid ? prevSnapshot : it));
            const status = e?.response?.status;
            if (status && status >= 500) toast.error('Une erreur serveur empêche la réaction. Réessayez dans un instant.');
            else if (status === 429) toast.error('Vous réagissez trop vite. Patientez quelques secondes.');
            else if (status === 401 || status === 403) toast.error('Connexion requise pour réagir.');
            else toast.error('Réaction non enregistrée. Réessayez.');
        } finally { release(key); }
    };

    const handleSave = async (post) => {
        const cid = post.content_id || post.id;
        const key = `save:${cid}`;
        if (!guard(key)) return;
        const wasSaved = savedIds.has(cid);
        setSavedIds(s => { const n = new Set(s); wasSaved ? n.delete(cid) : n.add(cid); return n; });
        setItems(arr => arr.map(it => it.content_id === cid ? {...it, _saved: !wasSaved} : it));
        try {
            if (wasSaved) await api.delete(`/posts/${cid}/save`);
            else await api.post(`/posts/${cid}/save`);
        } catch (e) {
            // revert
            setSavedIds(s => { const n = new Set(s); wasSaved ? n.add(cid) : n.delete(cid); return n; });
            setItems(arr => arr.map(it => it.content_id === cid ? {...it, _saved: wasSaved} : it));
            const status = e?.response?.status;
            if (status && status >= 500) toast.error('Erreur serveur. La sauvegarde n\'a pas été enregistrée.');
            else if (status === 401) toast.error('Connexion requise pour sauvegarder.');
            else toast.error('Sauvegarde non enregistrée. Réessayez.');
        } finally { release(key); }
    };
    const handleShare = async (post) => {
        const cid = post.content_id || post.id;
        const url = `${window.location.origin}/reseau/fil?post=${cid}`;
        if (navigator.share) {
            try { await navigator.share({ title: post.metadata?.title || "Publication Homly", url }); }
            catch { /* user cancelled */ }
        } else {
            try { await navigator.clipboard.writeText(url); toast.success('Lien copié'); } catch { toast.error('Copie impossible'); }
        }
        api.post(`/posts/${cid}/share`, { share_type: "external", external_channel: "copy_link" }).catch(()=>{});
    };
    const handleHide = async (post) => {
        const key = `hide:${post.id}`;
        if (!guard(key)) return;
        const snapshot = post;
        const index = items.findIndex(it => it.id === post.id);
        setItems(arr => arr.filter(it => it.id !== post.id));
        try {
            await api.post(`/feed/items/${post.id}/hide`, { content_id: post.content_id });
        } catch (e) {
            // restore at original position
            setItems(arr => {
                const next = [...arr];
                next.splice(index, 0, snapshot);
                return next;
            });
            const status = e?.response?.status;
            if (status && status >= 500) toast.error('Erreur serveur. La publication n\'a pas pu être masquée.');
            else toast.error('Publication non masquée. Réessayez.');
        } finally { release(key); }
    };
    const handleReport = async (post, reason) => {
        const key = `report:${post.id}`;
        if (!guard(key)) return;
        try {
            await api.post("/posts/reports", {
                target_type: "post", target_id: post.content_id || post.id, reason
            });
            toast.success('Merci, votre signalement a été pris en compte.');
        } catch (e) {
            const status = e?.response?.status;
            if (status && status >= 500) toast.error('Erreur serveur. Signalement non enregistré.');
            else toast.error('Signalement non enregistré. Réessayez.');
        } finally { release(key); }
    };

    return (
        <ReseauLayout user={user} headerTitle="Fil local" headerSubtitle="Publications territorialisées de votre réseau professionnel">
            <div
                className="mx-auto px-4 sm:px-6 py-6 grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-6"
                style={{ maxWidth: 'var(--reseau-maxw-page)' }}
            >
                <main>
                    {user && ["agent", "admin", "super_admin", "internal", "group_manager"].includes(user.role) && (
                        <button onClick={() => setComposerOpen(true)}
                                data-testid="open-composer-btn"
                                className="w-full mb-4 reseau-card reseau-card-hoverable p-4 flex items-center gap-3 text-left">
                            <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold"
                                 style={{ background: 'linear-gradient(135deg, var(--reseau-violet), var(--reseau-orange))' }}>
                                {getInitials(user)}
                            </div>
                            <span style={{ color: 'var(--reseau-ink-muted)' }}>Que souhaitez-vous partager ?</span>
                            <Edit3 className="w-4 h-4 ml-auto" style={{ color: 'var(--reseau-ink-muted)' }} />
                        </button>
                    )}

                    {territoryInfo && (
                        <div className="mb-4 p-3 reseau-card flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <MapPin className="w-4 h-4" style={{ color: 'var(--reseau-violet)' }} />
                                <span className="font-medium" style={{ color: 'var(--reseau-ink)' }}>
                                    Fil local · {territoryInfo.name}
                                </span>
                            </div>
                            <Link to="/reseau/fil" className="text-sm hover:underline" style={{ color: 'var(--reseau-violet)' }}>
                                Voir tout
                            </Link>
                        </div>
                    )}

                    <AnimatePresence>
                        {items.map(it => (
                            <PostCard key={it.id} post={it} dataTestid={`post-${it.content_id}`}
                                      onReact={handleReact} onSave={handleSave}
                                      onShare={handleShare} onHide={handleHide}
                                      onReport={handleReport}
                                      onOpenComments={(p) => setOpenCommentsFor(p)} />
                        ))}
                    </AnimatePresence>

                    {loading && (
                        <div className="text-center py-8" data-testid="feed-loading">
                            <Loader2 className="w-6 h-6 animate-spin mx-auto" style={{ color: 'var(--reseau-violet)' }} />
                        </div>
                    )}
                    {!loading && nextCursor && (
                        <div className="text-center py-4">
                            <Button variant="outline" data-testid="feed-load-more-btn"
                                    onClick={() => load(nextCursor)}
                                    className="reseau-btn reseau-btn-ghost">
                                Charger plus
                            </Button>
                        </div>
                    )}

                    {!loading && items.length === 0 && emptyState && (
                        <EmptyState state={emptyState} user={user} onCompose={() => setComposerOpen(true)} />
                    )}
                </main>

                <aside className="hidden lg:block">
                    <TerritoryIncitationCard user={user} />
                    <CockpitDigest user={user} />
                </aside>
            </div>

            <PostComposer open={composerOpen} onClose={() => setComposerOpen(false)}
                          onPublished={() => load()} />

            {openCommentsFor && (
                <CommentsPanel post={openCommentsFor}
                               onClose={() => setOpenCommentsFor(null)}
                               onPosted={() => load()} />
            )}
        </ReseauLayout>
    );
}
